import { useState } from "react";
import { FileText, Eye, Download, Image as ImageIcon } from "lucide-react";
import { Reveal } from "./Reveal";
import { useSite } from "../context/SiteContext";
import { viewDataUrl, downloadDataUrl } from "../lib/file";

export default function Syllabus() {
  const { syllabus } = useSite();
  const cats = ["All", ...Array.from(new Set((syllabus || []).map((s) => s.category).filter(Boolean)))];
  const [cat, setCat] = useState("All");
  const shown = cat === "All" ? syllabus : (syllabus || []).filter((s) => s.category === cat);

  return (
    <section data-testid="syllabus-section" className="bg-bone py-20 md:py-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Academics</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-forest leading-none">Syllabus</h2>
          <p className="text-muted mt-5 max-w-xl">Download the official syllabus for each department and programme, uploaded by the college.</p>
        </Reveal>

        {cats.length > 1 && (
          <div className="flex items-center gap-2 mt-10 mb-2 flex-wrap">
            {cats.map((c) => (
              <button key={c} data-testid="syllabus-cat-tab" onClick={() => setCat(c)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${cat === c ? "bg-forest text-bone border-forest" : "border-line text-muted hover:border-forest"}`}>{c}</button>
            ))}
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((s, i) => (
            <Reveal key={s.id || i} delay={(i % 3) * 0.05}>
              <div data-testid="syllabus-item" className="bg-white border border-line rounded-sm p-6 flex flex-col h-full hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-3">
                  <span className="w-11 h-11 rounded-sm bg-forest/5 text-forest flex items-center justify-center shrink-0">
                    {s.file_type === "image" ? <ImageIcon size={20} /> : <FileText size={20} />}
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-serif text-xl text-forest leading-tight">{s.title}</h3>
                    {s.category && <span className="inline-block mt-1 text-[10px] uppercase tracking-widest text-gold border border-gold/40 px-2 py-0.5 rounded-full">{s.category}</span>}
                  </div>
                </div>
                {s.file_name && <p className="text-xs text-muted mt-4 truncate">{s.file_name}</p>}
                <div className="flex items-center gap-2 mt-auto pt-5">
                  <button type="button" data-testid="syllabus-view-btn" onClick={() => viewDataUrl(s.file)} className="flex-1 inline-flex items-center justify-center gap-2 border border-forest text-forest px-4 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-forest hover:text-bone transition-colors">
                    <Eye size={14} /> View
                  </button>
                  <button type="button" data-testid="syllabus-download-btn" onClick={() => downloadDataUrl(s.file, s.file_name || `${s.title}.pdf`)} className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-forest px-4 py-2.5 rounded-full text-xs uppercase tracking-widest hover:-translate-y-0.5 transition-transform">
                    <Download size={14} /> Download
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        {(!shown || shown.length === 0) && <p className="mt-8 text-muted">No syllabus uploaded yet. Please check back soon.</p>}
      </div>
    </section>
  );
}
